import type {
  BillingPeriod,
  EntityStatus,
  PlanCategory,
  TargetAudience,
} from '@monorepo/shared/schemas/common'
import { CHARGE_TYPE } from '@monorepo/shared/schemas/common'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  type CreatePlan,
  type CreatePlanAgentAction,
  type CreatePlanCharge,
  type CreatePlanFeature,
  type CreatePricingTier,
  type PlanQuery,
  planService,
  type UpdatePlan,
  type UpdatePlanAgentAction,
  type UpdatePlanCharge,
  type UpdatePlanFeature,
  type UpdatePricingTier,
} from '../services/planService'
import type { PlanFilters } from '../types/plan.types'

interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: string
      }
      message?: string
    }
  }
  message?: string
}

export const PLANS_QUERY_KEY = 'plans'

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Get all plans with optional filters
 */
export function usePlans(query?: PlanQuery) {
  return useQuery({
    queryKey: [PLANS_QUERY_KEY, query],
    queryFn: () => planService.getPlans(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Get single plan by ID (includes nested charges, features, agent actions)
 */
export function usePlan(id: string) {
  return useQuery({
    queryKey: [PLANS_QUERY_KEY, id],
    queryFn: () => planService.getPlan(id),
    enabled: !!id,
  })
}

// ============================================================================
// MUTATION HOOKS - PLANS
// ============================================================================

/**
 * Create new plan
 */
export function useCreatePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePlan) => planService.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLANS_QUERY_KEY] })
      toast.success('Plan created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create plan'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update plan
 */
export function useUpdatePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePlan }) =>
      planService.updatePlan(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PLANS_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.id],
      })
      toast.success('Plan updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update plan'
      toast.error(errorMessage)
    },
  })
}

/**
 * Delete plan
 */
export function useDeletePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => planService.deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLANS_QUERY_KEY] })
      toast.success('Plan deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete plan'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update plan status
 */
export function useUpdatePlanStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: EntityStatus }) =>
      planService.updateStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PLANS_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.id],
      })
      toast.success('Plan status updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update plan status'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// MUTATION HOOKS - PLAN CHARGES
// ============================================================================

/**
 * Add charge to plan
 */
export function useAddCharge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: string
      data: CreatePlanCharge
    }) => planService.addCharge(planId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Charge added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add charge'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update charge
 */
export function useUpdateCharge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      chargeId,
      data,
    }: {
      planId: string
      chargeId: string
      data: UpdatePlanCharge
    }) => planService.updateCharge(planId, chargeId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Charge updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update charge'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove charge from plan
 */
export function useRemoveCharge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ planId, chargeId }: { planId: string; chargeId: string }) =>
      planService.removeCharge(planId, chargeId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Charge removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove charge'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// MUTATION HOOKS - PLAN FEATURES
// ============================================================================

/**
 * Add feature to plan
 */
export function useAddFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: string
      data: CreatePlanFeature
    }) => planService.addFeature(planId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Feature added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add feature'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update plan feature
 */
export function useUpdateFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      data,
    }: {
      planId: string
      featureId: string
      data: UpdatePlanFeature
    }) => planService.updateFeature(planId, featureId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Feature updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update feature'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove feature from plan
 */
export function useRemoveFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
    }: {
      planId: string
      featureId: string
    }) => planService.removeFeature(planId, featureId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Feature removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove feature'
      toast.error(errorMessage)
    },
  })
}

/**
 * Add pricing tier to feature
 */
export function useAddFeatureTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      data,
    }: {
      planId: string
      featureId: string
      data: CreatePricingTier
    }) => planService.addFeatureTier(planId, featureId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update feature pricing tier
 */
export function useUpdateFeatureTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      tierId,
      data,
    }: {
      planId: string
      featureId: string
      tierId: string
      data: UpdatePricingTier
    }) => planService.updateFeatureTier(planId, featureId, tierId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove pricing tier from feature
 */
export function useRemoveFeatureTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      featureId,
      tierId,
    }: {
      planId: string
      featureId: string
      tierId: string
    }) => planService.removeFeatureTier(planId, featureId, tierId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove pricing tier'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// MUTATION HOOKS - PLAN AGENT ACTIONS
// ============================================================================

/**
 * Add agent action to plan
 */
export function useAddAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: string
      data: CreatePlanAgentAction
    }) => planService.addAgentAction(planId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Agent action added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add agent action'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update plan agent action
 */
export function useUpdateAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      data,
    }: {
      planId: string
      agentActionId: string
      data: UpdatePlanAgentAction
    }) => planService.updateAgentAction(planId, agentActionId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Agent action updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update agent action'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove agent action from plan
 */
export function useRemoveAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
    }: {
      planId: string
      agentActionId: string
    }) => planService.removeAgentAction(planId, agentActionId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Agent action removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove agent action'
      toast.error(errorMessage)
    },
  })
}

/**
 * Add pricing tier to agent action
 */
export function useAddAgentActionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      data,
    }: {
      planId: string
      agentActionId: string
      data: CreatePricingTier
    }) => planService.addAgentActionTier(planId, agentActionId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier added successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to add pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Update agent action pricing tier
 */
export function useUpdateAgentActionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      tierId,
      data,
    }: {
      planId: string
      agentActionId: string
      tierId: string
      data: UpdatePricingTier
    }) =>
      planService.updateAgentActionTier(planId, agentActionId, tierId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update pricing tier'
      toast.error(errorMessage)
    },
  })
}

/**
 * Remove pricing tier from agent action
 */
export function useRemoveAgentActionTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      agentActionId,
      tierId,
    }: {
      planId: string
      agentActionId: string
      tierId: string
    }) => planService.removeAgentActionTier(planId, agentActionId, tierId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Pricing tier removed successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove pricing tier'
      toast.error(errorMessage)
    },
  })
}

// ============================================================================
// STUB HOOKS FOR LEGACY COMPONENTS (To be implemented later)
// ============================================================================

/**
 * Plan filters hook - converts UI filters to API query format
 */
export function usePlanFilters() {
  const [filters, setFilters] = useState<PlanFilters>({
    status: 'all',
    billingPeriod: 'all',
    targetAudience: 'all',
    type: 'all',
    page: 1,
    limit: 20,
  })

  const updateFilters = (newFilters: Partial<PlanFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  // Convert UI filters (camelCase) to API query (snake_case)
  const toAPIQuery = (uiFilters: PlanFilters): PlanQuery => {
    const query: PlanQuery = {}

    // Search
    if (uiFilters.search) {
      query.search = uiFilters.search
    }

    // Status (filter out 'all')
    if (uiFilters.status && uiFilters.status !== 'all') {
      query.status = uiFilters.status as EntityStatus
    }

    // Target Audience (filter out 'all')
    if (uiFilters.targetAudience && uiFilters.targetAudience !== 'all') {
      query.target_audience = uiFilters.targetAudience as TargetAudience
    }

    // Billing Period (convert UI values to API enum, filter out 'all')
    if (uiFilters.billingPeriod && uiFilters.billingPeriod !== 'all') {
      // Convert UI values to API enum values
      const periodMap: Record<string, BillingPeriod> = {
        monthly: 'monthly' as BillingPeriod,
        quarterly: 'quarterly' as BillingPeriod,
        yearly: 'annual' as BillingPeriod, // UI uses 'yearly', API expects 'annual'
      }
      query.billing_period =
        periodMap[uiFilters.billingPeriod] ||
        (uiFilters.billingPeriod as BillingPeriod)
    }

    // Category/Type (filter out 'all')
    if (uiFilters.type && uiFilters.type !== 'all') {
      query.category = uiFilters.type as PlanCategory
    }

    // Pagination
    if (uiFilters.page) {
      query.page = uiFilters.page
    }
    if (uiFilters.limit) {
      query.limit = uiFilters.limit
    }

    // Sorting
    if (uiFilters.sortBy) {
      query.order = uiFilters.sortOrder || 'desc'
    }

    return query
  }

  return {
    filters,
    updateFilters,
    setFilters,
    apiQuery: toAPIQuery(filters),
  }
}

/**
 * Stub hook for plan charges - used by legacy components
 * Memoized to prevent creating new object references on every render
 */
export function usePlanCharges(planId: string) {
  const { data: plan } = usePlan(planId)

  // Memoize the return value to prevent infinite loops in components that depend on this
  return useMemo(() => {
    const charges = plan?.charges || []
    const oneTimeCharges = charges.filter(
      c => c.charge_type === CHARGE_TYPE.one_time
    )
    const recurringCharges = charges.filter(
      c => c.charge_type === CHARGE_TYPE.recurring
    )

    return {
      data: {
        oneTimeCharges,
        recurringCharges,
      },
      isLoading: false,
    }
  }, [plan?.charges]) // Only recompute when charges actually change
}

/**
 * Stub hook for plan features - used by legacy components
 * Memoized to prevent creating new object references on every render
 */
export function usePlanFeatures(planId: string) {
  const { data: plan } = usePlan(planId)

  // Memoize the return value to prevent infinite loops in components that depend on this
  return useMemo(
    () => ({
      data: plan?.features || [],
      isLoading: false,
    }),
    [plan?.features]
  ) // Only recompute when features actually change
}

/**
 * Stub hook for plan trial - used by legacy components
 * Memoized to prevent creating new object references on every render
 */
export function usePlanTrial(planId: string) {
  const { data: plan } = usePlan(planId)

  // Memoize the return value to prevent infinite loops in components that depend on this
  return useMemo(
    () => ({
      data: {
        trial_available: plan?.trial_available || false,
        trial_days: plan?.trial_days || 0,
      },
      isLoading: false,
    }),
    [plan?.trial_available, plan?.trial_days]
  ) // Only recompute when trial settings actually change
}

/**
 * Stub mutation for updating plan trial
 */
export function useUpdatePlanTrial() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      trialData,
    }: {
      planId: string
      trialData: { trial_available: boolean; trial_days: number }
    }) => planService.updatePlan(planId, trialData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PLANS_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [PLANS_QUERY_KEY, variables.planId],
      })
      toast.success('Trial settings updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update trial settings'
      toast.error(errorMessage)
    },
  })
}

/**
 * Hook for features list - used by legacy components
 */
export function useFeaturesList() {
  return useQuery({
    queryKey: ['features'],
    queryFn: async () => {
      // Import the feature service dynamically to avoid circular dependencies
      const { featureService } = await import(
        '@/features/features/services/featureService'
      )
      const response = await featureService.getFeatures()
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
